import React, { useMemo, useState } from 'react';
import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import PublicContentPageShell from '@components/layout/PublicContentPageShell';
import { brandColors } from '@config/brandTokens';
import {
  FITNESS_OVERVIEW_HEADERS,
  FITNESS_OVERVIEW_INTRO,
  FITNESS_OVERVIEW_ROWS,
  FITNESS_PAGE_SUBTITLE,
  FITNESS_PAGE_TITLE,
  FITNESS_SKILLS_HEADERS,
  FITNESS_SKILLS_INTRO,
  FITNESS_SKILLS_ROWS,
} from '@/content/fitnessStandardsData';

type PageTab = 'overview' | 'skills';

const OVERVIEW_COUNTRY_KEY = 'Country / region';
const OVERVIEW_TYPE_KEY = 'Type';
const OVERVIEW_PROGRAM_KEY = 'Program';

const SKILLS_DOMAIN_KEY = 'Master domain';
const SKILLS_SUBDOMAIN_KEY = 'Explicit subdomain / skill';
const SKILLS_TEST_KEY = 'Named test or capability';
const SKILLS_UNIT_KEY = 'Reported unit / output';
const SKILLS_SOURCE_KEY = 'Official source URL(s)';

const SKILLS_CORE_HEADERS = [
  SKILLS_DOMAIN_KEY,
  SKILLS_SUBDOMAIN_KEY,
  SKILLS_TEST_KEY,
  SKILLS_UNIT_KEY,
] as const;

const SKILLS_PROGRAM_HEADERS = FITNESS_SKILLS_HEADERS.filter(
  (h) =>
    !SKILLS_CORE_HEADERS.includes(h as (typeof SKILLS_CORE_HEADERS)[number]) &&
    h !== SKILLS_SOURCE_KEY,
);

function hasValue(v: string | undefined): boolean {
  const t = String(v || '').trim();
  return Boolean(t) && t !== '—' && t !== '-';
}

function uniqueSorted(values: string[]): string[] {
  return [...new Set(values.map((v) => v.trim()).filter(Boolean))].sort((a, b) =>
    a.localeCompare(b),
  );
}

function renderCell(value: string) {
  const text = String(value || '').trim();
  if (!text || text === '—') {
    return (
      <Typography component="span" sx={{ color: '#94a3b8' }}>
        —
      </Typography>
    );
  }
  const urls = text.match(/https?:\/\/[^\s]+/g);
  if (urls && urls.length === 1 && text === urls[0]) {
    return (
      <Link href={urls[0]} target="_blank" rel="noopener noreferrer" sx={{ fontWeight: 600, fontSize: '0.82rem' }}>
        Open source
      </Link>
    );
  }
  if (urls && urls.length) {
    return (
      <Box>
        <Typography sx={{ color: brandColors.slate, fontSize: '0.82rem', whiteSpace: 'pre-wrap', mb: 0.5 }}>
          {text.replace(/https?:\/\/[^\s]+/g, '').trim()}
        </Typography>
        {urls.map((url) => (
          <Link
            key={url}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            display="block"
            sx={{ fontSize: '0.75rem', fontWeight: 600 }}
          >
            {url.replace(/^https?:\/\//, '').slice(0, 48)}…
          </Link>
        ))}
      </Box>
    );
  }
  return (
    <Typography sx={{ color: brandColors.slate, fontSize: '0.82rem', whiteSpace: 'pre-wrap', lineHeight: 1.4 }}>
      {text}
    </Typography>
  );
}

const FitnessStandardsPage: React.FC = () => {
  const [tab, setTab] = useState<PageTab>('overview');

  const [overviewCountry, setOverviewCountry] = useState('all');
  const [overviewType, setOverviewType] = useState('all');
  const [overviewSearch, setOverviewSearch] = useState('');

  const [skillsDomain, setSkillsDomain] = useState('all');
  const [skillsProgram, setSkillsProgram] = useState('all');
  const [skillsSearch, setSkillsSearch] = useState('');

  const overviewDataRows = useMemo(
    () =>
      FITNESS_OVERVIEW_ROWS.filter((row) => {
        const country = String(row[OVERVIEW_COUNTRY_KEY] || '');
        return country && !country.toLowerCase().startsWith('legend');
      }),
    [],
  );

  const overviewCountries = useMemo(
    () => uniqueSorted(overviewDataRows.map((r) => r[OVERVIEW_COUNTRY_KEY] || '')),
    [overviewDataRows],
  );

  const overviewTypes = useMemo(
    () => uniqueSorted(overviewDataRows.map((r) => r[OVERVIEW_TYPE_KEY] || '')),
    [overviewDataRows],
  );

  const skillsDomains = useMemo(
    () => uniqueSorted(FITNESS_SKILLS_ROWS.map((r) => r[SKILLS_DOMAIN_KEY] || '')),
    [],
  );

  const filteredOverview = useMemo(() => {
    const q = overviewSearch.trim().toLowerCase();
    return overviewDataRows.filter((row) => {
      const country = String(row[OVERVIEW_COUNTRY_KEY] || '').trim();
      const type = String(row[OVERVIEW_TYPE_KEY] || '').trim();
      if (overviewCountry !== 'all' && country !== overviewCountry) return false;
      if (overviewType !== 'all' && type !== overviewType) return false;
      if (q) {
        const hay = [
          row[OVERVIEW_COUNTRY_KEY],
          row[OVERVIEW_PROGRAM_KEY],
          row['Age coverage'],
          row['Main tests / domains'],
          row['Values / scoring'],
          row['Platform reference use'],
        ]
          .join(' ')
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [overviewDataRows, overviewCountry, overviewType, overviewSearch]);

  const filteredSkills = useMemo(() => {
    const q = skillsSearch.trim().toLowerCase();
    return FITNESS_SKILLS_ROWS.filter((row) => {
      const domain = String(row[SKILLS_DOMAIN_KEY] || '').trim();
      if (skillsDomain !== 'all' && domain !== skillsDomain) return false;
      if (skillsProgram !== 'all' && !hasValue(row[skillsProgram])) return false;
      if (q) {
        const hay = [
          row[SKILLS_DOMAIN_KEY],
          row[SKILLS_SUBDOMAIN_KEY],
          row[SKILLS_TEST_KEY],
          row[SKILLS_UNIT_KEY],
        ]
          .join(' ')
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [skillsDomain, skillsProgram, skillsSearch]);

  const skillsVisibleHeaders = useMemo(() => {
    if (skillsProgram === 'all') return FITNESS_SKILLS_HEADERS;
    return [...SKILLS_CORE_HEADERS, skillsProgram, SKILLS_SOURCE_KEY];
  }, [skillsProgram]);

  const headers = tab === 'overview' ? FITNESS_OVERVIEW_HEADERS : skillsVisibleHeaders;
  const rows = tab === 'overview' ? filteredOverview : filteredSkills;
  const intro = tab === 'overview' ? FITNESS_OVERVIEW_INTRO : FITNESS_SKILLS_INTRO;
  const totalCount = tab === 'overview' ? overviewDataRows.length : FITNESS_SKILLS_ROWS.length;

  const overviewFiltersActive =
    overviewCountry !== 'all' || overviewType !== 'all' || overviewSearch.trim() !== '';
  const skillsFiltersActive =
    skillsDomain !== 'all' || skillsProgram !== 'all' || skillsSearch.trim() !== '';

  return (
    <PublicContentPageShell maxWidth={false} title={FITNESS_PAGE_TITLE} subtitle={FITNESS_PAGE_SUBTITLE}>
      <Box
        role="tablist"
        aria-label="Fitness standards sections"
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: { xs: 0.5, md: 1 },
          mb: 2,
          borderBottom: '2px solid #e2e8f0',
        }}
      >
        {(
          [
            { id: 'overview' as const, label: 'Overview' },
            { id: 'skills' as const, label: 'Consolidated Skills' },
          ] as const
        ).map((item) => {
          const active = tab === item.id;
          return (
            <Box
              key={item.id}
              component="button"
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setTab(item.id)}
              sx={{
                appearance: 'none',
                border: 'none',
                borderBottom: active ? `3px solid ${brandColors.navy}` : '3px solid transparent',
                bgcolor: 'transparent',
                color: active ? brandColors.navy : brandColors.slate,
                fontFamily: '"Sora", Arial, sans-serif',
                fontWeight: active ? 800 : 600,
                fontSize: { xs: '0.92rem', md: '1rem' },
                px: { xs: 1.25, md: 1.75 },
                pt: 0.75,
                pb: 1.15,
                mb: '-2px',
                cursor: 'pointer',
                lineHeight: 1.3,
                borderRadius: 0,
                '&:hover': { color: brandColors.navy, bgcolor: 'transparent' },
              }}
            >
              {item.label}
            </Box>
          );
        })}
      </Box>

      <Typography sx={{ color: brandColors.slate, mb: 1.5, fontSize: '0.92rem', lineHeight: 1.55, maxWidth: 1100 }}>
        {intro}
      </Typography>

      {tab === 'overview' ? (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.25}
          useFlexGap
          flexWrap="wrap"
          sx={{ mb: 1.5 }}
          alignItems={{ sm: 'center' }}
        >
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="fitness-overview-country-label">Country / region</InputLabel>
            <Select
              labelId="fitness-overview-country-label"
              label="Country / region"
              value={overviewCountry}
              onChange={(e) => setOverviewCountry(String(e.target.value))}
            >
              <MenuItem value="all">All countries / regions</MenuItem>
              {overviewCountries.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel id="fitness-overview-type-label">Type</InputLabel>
            <Select
              labelId="fitness-overview-type-label"
              label="Type"
              value={overviewType}
              onChange={(e) => setOverviewType(String(e.target.value))}
            >
              <MenuItem value="all">All types</MenuItem>
              {overviewTypes.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Search"
            placeholder="Program, tests, scoring…"
            value={overviewSearch}
            onChange={(e) => setOverviewSearch(e.target.value)}
            sx={{ minWidth: { xs: '100%', sm: 240 }, flex: 1 }}
          />
          {overviewFiltersActive ? (
            <Chip
              label="Clear filters"
              onDelete={() => {
                setOverviewCountry('all');
                setOverviewType('all');
                setOverviewSearch('');
              }}
              sx={{ fontWeight: 700 }}
            />
          ) : null}
        </Stack>
      ) : (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.25}
          useFlexGap
          flexWrap="wrap"
          sx={{ mb: 1.5 }}
          alignItems={{ sm: 'center' }}
        >
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="fitness-skills-domain-label">Master domain</InputLabel>
            <Select
              labelId="fitness-skills-domain-label"
              label="Master domain"
              value={skillsDomain}
              onChange={(e) => setSkillsDomain(String(e.target.value))}
            >
              <MenuItem value="all">All domains</MenuItem>
              {skillsDomains.map((d) => (
                <MenuItem key={d} value={d}>
                  {d}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel id="fitness-skills-program-label">Country program</InputLabel>
            <Select
              labelId="fitness-skills-program-label"
              label="Country program"
              value={skillsProgram}
              onChange={(e) => setSkillsProgram(String(e.target.value))}
            >
              <MenuItem value="all">All programs (all columns)</MenuItem>
              {SKILLS_PROGRAM_HEADERS.map((p) => (
                <MenuItem key={p} value={p}>
                  {p}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Search"
            placeholder="Skill, test, unit…"
            value={skillsSearch}
            onChange={(e) => setSkillsSearch(e.target.value)}
            sx={{ minWidth: { xs: '100%', sm: 220 }, flex: 1 }}
          />
          {skillsFiltersActive ? (
            <Chip
              label="Clear filters"
              onDelete={() => {
                setSkillsDomain('all');
                setSkillsProgram('all');
                setSkillsSearch('');
              }}
              sx={{ fontWeight: 700 }}
            />
          ) : null}
        </Stack>
      )}

      <Typography sx={{ color: brandColors.slate, mb: 1.5, fontSize: '0.85rem', fontWeight: 600 }}>
        Showing {rows.length} of {totalCount} · {headers.length} columns
      </Typography>

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          border: '1px solid #e2e8f0',
          borderRadius: 2,
          width: '100%',
          maxHeight: { xs: '70vh', md: 'calc(100vh - 320px)' },
          overflow: 'auto',
        }}
      >
        <Table size="small" stickyHeader sx={{ minWidth: Math.max(960, headers.length * 140) }}>
          <TableHead>
            <TableRow>
              {headers.map((header, idx) => (
                <TableCell
                  key={header}
                  sx={{
                    fontWeight: 800,
                    bgcolor: '#f1f5f9',
                    color: brandColors.navy,
                    whiteSpace: 'nowrap',
                    minWidth: idx < 3 ? 140 : 160,
                    position: idx === 0 ? 'sticky' : undefined,
                    left: idx === 0 ? 0 : undefined,
                    zIndex: idx === 0 ? 3 : 2,
                  }}
                >
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={headers.length} sx={{ color: brandColors.slate, py: 3 }}>
                  No rows match the current filters.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, rowIdx) => (
                <TableRow key={`${tab}-${rowIdx}`} hover>
                  {headers.map((header, colIdx) => (
                    <TableCell
                      key={`${rowIdx}-${header}`}
                      sx={{
                        verticalAlign: 'top',
                        position: colIdx === 0 ? 'sticky' : undefined,
                        left: colIdx === 0 ? 0 : undefined,
                        bgcolor: colIdx === 0 ? brandColors.white : undefined,
                        zIndex: colIdx === 0 ? 1 : undefined,
                        fontWeight: colIdx === 0 ? 700 : 400,
                        color: colIdx === 0 ? brandColors.navy : undefined,
                        maxWidth: colIdx === 0 ? 200 : 320,
                      }}
                    >
                      {renderCell(row[header] || '')}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </PublicContentPageShell>
  );
};

export default FitnessStandardsPage;
